"use client";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import AdminHeader from "../../components/organisms/AdminHeader";
import AdminCompanyBanner from "../../components/molecules/AdminCompanyBanner";
import AdminJobTabs from "../../components/molecules/AdminJobTabs";
import AdminJobCard from "../../components/molecules/AdminJobCard";
import SectionBox from "../../components/layout/SectionBox";
import Footer from "../../components/organisms/Footer";
import Button from "../../components/atoms/Button";
import { JobAdmin } from "../../types/jobAdmin";

import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";
import { useJobPostStore } from "../../store/useJobPostStore"; // ← WAJIB

export default function AdminJobPost() {
    const location = useLocation();
    const lang = location.pathname.startsWith("/id") ? "id" : "en";

    // Job store
    const { jobs, fetchAllJobs, loading } = useJobPostStore();

    // UI states
    const [active, setActive] = useState<"draft" | "reviewed" | "open" | "rejected" | "closed">("draft");
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchAllJobs(lang);
    }, [lang]);

    const filtered = jobs[active]?.filter((j) =>
        j.name.en.toLowerCase().includes(search.toLowerCase())
    ) || [];

    const counts = {
        draft: jobs["draft"]?.length || 0,
        "reviewed": jobs["reviewed"]?.length || 0,
        open: jobs["open"]?.length || 0,
        closed: jobs["closed"]?.length || 0,
        rejected: jobs["rejected"]?.length || 0,
    };


    // Company Profile store
    const profile = useCompanyProfileStore((state) => state.profile);
    const fetchCompanyProfile = useCompanyProfileStore((state) => state.fetchCompanyProfile);

    useEffect(() => {
        fetchCompanyProfile();
    }, [lang, fetchCompanyProfile]);

    return (
        <>
            <AdminHeader />

            <main>
                <div className="container-section mx-auto px-6 pt-6 pb-6 ">
                    <SectionBox className="mt-24">
                        {/* Company Banner */}
                        {!profile ? (
                            <p className="text-gray-500">Loading company data...</p>
                        ) : (
                            <AdminCompanyBanner
                                name={profile.name}
                                industry={profile.industry_id}
                                size={profile.company_size_id}
                                logo={profile.logo}
                                end_date={profile.active_day}
                                start_date={profile.start_date}
                                slot_posting={profile.slot_posting}
                                package_name={profile.package_name}
                                package_id={profile.package_id}
                                website={profile.website}
                                lang={lang}
                                phoneNumber={profile.phone}
                            />
                        )}

                        {/* Tabs */}
                        <AdminJobTabs active={active} setActive={setActive} counts={counts} lang={lang} />

                        {/* Search & New Job button */}
                        <div className="mt-6 flex flex-col md:flex-row justify-between md:justify-start items-start md:items-center gap-4">
                            <input
                                type="text"
                                placeholder={lang === "id" ? "Cari Judul Pekerjaan" : "Search by Job Title"}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="border px-4 py-2 rounded-lg w-full md:w-64"
                            />

                            <Button textClass="text-white" href={`/${lang}/admin/job-post/create`}>
                                + {lang === "id" ? "Tambah Pekerjaan" : "New Job"}
                            </Button>
                        </div>

                        {/* Job Cards */}
                        <div className="mt-4">
                            {loading && <p className="text-gray-500">Loading jobs...</p>}
                            {!loading && filtered.length === 0 && (
                                <p className="text-gray-500">No jobs found.</p>
                            )}

                            {!loading &&
                                filtered.map((job, idx) => {
                                    // Map status
                                    const mapStatus = (val?: string): JobAdmin["status"] => {
                                        if (!val) return "rejected"; // fallback kalau undefined
                                        switch (val.toLowerCase()) {
                                            case "open": return "open";
                                            case "closed": return "closed";
                                            case "draft": return "draft";
                                            case "reviewed": return "reviewed";
                                            default: return "rejected";
                                        }
                                    };

                                    const formatDate = (dateStr?: string) => {
                                        if (!dateStr) return "-";
                                        try {
                                            const date = new Date(dateStr);
                                            return new Intl.DateTimeFormat(lang === "id" ? "id-ID" : "en-US", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            }).format(date);
                                        } catch {
                                            return "-";
                                        }
};

                                    const mapped: JobAdmin = {
                                        id: job.id,
                                        job_title: lang === "en" ? job.name?.en ?? "" : job.name?.id ?? "",
                                        city:  job.city?.name ?? "-",
                                        country:  job.country?.name ?? "-",
                                        state:  job.state?.name ?? "-",
                                        min_salary: job.min_salary ?? 0,
                                        max_salary: job.max_salary ?? 0,
                                        status: mapStatus(job.status),
                                        closing: formatDate(job.expire_at) ?? "-",
                                        applicants: job.total_applicants ?? 0,
                                        created: job.created_at ?? "-", 
                                        companyProfile: job.company_name ?? "",
                                        size: job.company_size_id ?? null,
                                        degree: job.degree?.id?.[lang] ?? null,
                                        experience: job.experience_year_id ?? null,
                                        jobType: job.job_type_id ?? null,
                                        working_type: job.working_type_id ?? null,
                                        responsibility: job.responsibility?.[lang] ?? "",
                                        requirement: job.requirement?.[lang] ?? "",
                                        skill: job.skills?.map((s) => s.name).join(", ") ?? null,
                                        undisclosed: !job.show_salary,
                                    };


                                    return <AdminJobCard key={idx} job={mapped} />;
                                })}
                        </div>
                    </SectionBox>
                </div>
            </main>

            <Footer />
        </>
    );
}
